import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogFooter 
} from '../../../components/ui/dialog';
import { 
  Star, 
  Plus, 
  Edit2, 
  Trash2, 
  Eye,
  EyeOff,
  GripVertical,
  ExternalLink,
  Mail,
  Phone,
  Link as LinkIcon,
  Image as ImageIcon,
  RefreshCw,
  Settings,
  Play,
  Pause,
  Clock
} from 'lucide-react';
import { toast } from 'sonner';
import * as api from '../../../services/api';
import ImagePickerDialog from './ImagePickerDialog';

const CTA_TYPES = [
  { id: 'none', label: 'Ingen knapp', icon: null },
  { id: 'contact', label: 'Kontaktformulär', icon: Phone },
  { id: 'url', label: 'Extern länk', icon: LinkIcon },
  { id: 'email', label: 'E-post', icon: Mail },
];

const FeaturedAnnouncementsManager = () => {
  const [loading, setLoading] = useState(true);
  const [announcements, setAnnouncements] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [showImagePicker, setShowImagePicker] = useState(false);
  const [settings, setSettings] = useState({
    auto_scroll_enabled: true,
    auto_scroll_interval: 5,
  });
  const [savingSettings, setSavingSettings] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    image_url: '',
    cta_type: 'none',
    cta_url: '',
    cta_email: '',
    cta_text: '',
    is_visible: true,
    order: 0,
  });

  const fetchAnnouncements = useCallback(async () => {
    try {
      const data = await api.getAdminFeaturedAnnouncements();
      setAnnouncements(data);
    } catch (error) {
      console.error('Error fetching featured announcements:', error);
      toast.error('Kunde inte hämta meddelanden');
    }
  }, []);

  const fetchSettings = useCallback(async () => {
    try {
      const data = await api.getFeaturedAnnouncementsSettings();
      setSettings(data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchAnnouncements(), fetchSettings()]);
      setLoading(false);
    };
    loadData();
  }, [fetchAnnouncements, fetchSettings]);

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      await api.updateFeaturedAnnouncementsSettings(settings);
      toast.success('Inställningar sparade');
    } catch (error) {
      toast.error('Kunde inte spara inställningar');
    } finally {
      setSavingSettings(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      content: '',
      image_url: '',
      cta_type: 'none',
      cta_url: '',
      cta_email: '',
      cta_text: '',
      is_visible: true,
      order: announcements.length,
    });
    setEditingAnnouncement(null);
  };

  const openCreateDialog = () => {
    resetForm();
    setShowDialog(true);
  };

  const openEditDialog = (announcement) => {
    setEditingAnnouncement(announcement);
    setFormData({
      title: announcement.title || '',
      content: announcement.content || '',
      image_url: announcement.image_url || '',
      cta_type: announcement.cta_type || 'none',
      cta_url: announcement.cta_url || '',
      cta_email: announcement.cta_email || '',
      cta_text: announcement.cta_text || '',
      is_visible: announcement.is_visible ?? true,
      order: announcement.order ?? 0,
    });
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!formData.title.trim()) {
      toast.error('Titel krävs');
      return;
    }

    try {
      if (editingAnnouncement) {
        await api.updateFeaturedAnnouncement(editingAnnouncement.id, formData);
        toast.success('Meddelande uppdaterat!');
      } else {
        await api.createFeaturedAnnouncement(formData);
        toast.success('Meddelande skapat!');
      }
      setShowDialog(false);
      resetForm();
      await fetchAnnouncements();
    } catch (error) {
      toast.error('Kunde inte spara meddelande');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Ta bort detta meddelande?')) return;
    
    try {
      await api.deleteFeaturedAnnouncement(id);
      toast.success('Meddelande borttaget!');
      await fetchAnnouncements();
    } catch (error) {
      toast.error('Kunde inte ta bort meddelande');
    }
  };

  const handleToggleVisibility = async (announcement) => {
    try {
      await api.updateFeaturedAnnouncement(announcement.id, {
        ...announcement,
        is_visible: !announcement.is_visible,
      });
      toast.success(announcement.is_visible ? 'Meddelande dolt' : 'Meddelande synligt');
      await fetchAnnouncements();
    } catch (error) {
      toast.error('Kunde inte ändra synlighet');
    }
  };

  const handleImageSelect = (url) => {
    setFormData({ ...formData, image_url: url });
    setShowImagePicker(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Utvalda meddelanden</h2>
          <p className="text-gray-600">Hantera framhävda meddelanden på startsidan (visas i 3 kolumner)</p>
        </div>
        <Button onClick={openCreateDialog} className="bg-amber-600 hover:bg-amber-700">
          <Plus className="w-4 h-4 mr-2" />
          Lägg till
        </Button>
      </div>

      {/* Auto-Scroll Settings */}
      <Card className="border-blue-200 bg-blue-50">
        <CardContent className="p-4">
          <div className="flex items-center gap-3 mb-3">
            <Settings className="w-5 h-5 text-blue-600" />
            <h3 className="font-medium text-blue-900">Auto-scroll inställningar</h3>
          </div>
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <button
                onClick={() => setSettings({ ...settings, auto_scroll_enabled: !settings.auto_scroll_enabled })}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors ${
                  settings.auto_scroll_enabled 
                    ? 'bg-green-100 border-green-300 text-green-700' 
                    : 'bg-gray-100 border-gray-300 text-gray-600'
                }`}
              >
                {settings.auto_scroll_enabled ? (
                  <>
                    <Play className="w-4 h-4" />
                    <span className="text-sm font-medium">Aktiverad</span>
                  </>
                ) : (
                  <>
                    <Pause className="w-4 h-4" />
                    <span className="text-sm font-medium">Avstängd</span>
                  </>
                )}
              </button>
            </div>
            
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-gray-500" />
              <span className="text-sm text-gray-600">Tid mellan bilder:</span>
              <select
                value={settings.auto_scroll_interval}
                onChange={(e) => setSettings({ ...settings, auto_scroll_interval: parseInt(e.target.value) })}
                className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
              >
                <option value={3}>3 sekunder</option>
                <option value={5}>5 sekunder</option>
                <option value={7}>7 sekunder</option>
                <option value={10}>10 sekunder</option>
                <option value={15}>15 sekunder</option>
              </select>
            </div>
            
            <Button 
              size="sm" 
              onClick={handleSaveSettings}
              disabled={savingSettings}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {savingSettings ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                'Spara'
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Preview Info */}
      <Card className="bg-amber-50 border-amber-200">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Star className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-amber-800">
              <p className="font-medium">Förhandsvisning</p>
              <p>Dessa meddelanden visas i en 3-kolumn layout på startsidan. På mobil visas de som en kolumn med navigeringsknappar.</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Announcements List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Star className="w-5 h-5 text-amber-600" />
            Meddelanden ({announcements.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {announcements.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Star className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>Inga utvalda meddelanden</p>
              <p className="text-sm mt-1">Lägg till ditt första meddelande</p>
            </div>
          ) : (
            <div className="space-y-4">
              {announcements.map((announcement, idx) => {
                const ctaType = CTA_TYPES.find(t => t.id === announcement.cta_type);
                const CtaIcon = ctaType?.icon;
                
                return (
                  <div
                    key={announcement.id}
                    className={`p-4 rounded-lg border ${
                      announcement.is_visible ? 'bg-white border-gray-200' : 'bg-gray-50 border-gray-200 opacity-60'
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      {/* Drag Handle (visual only for now) */}
                      <div className="flex-shrink-0 text-gray-400 mt-1">
                        <GripVertical className="w-5 h-5" />
                      </div>
                      
                      {/* Image Preview */}
                      {announcement.image_url && (
                        <div className="flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden bg-gray-100">
                          <img 
                            src={announcement.image_url} 
                            alt="" 
                            className="w-full h-full object-cover"
                          />
                        </div>
                      )}
                      
                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-semibold text-gray-900 truncate">{announcement.title}</h4>
                          {!announcement.is_visible && (
                            <span className="text-xs px-2 py-0.5 rounded bg-gray-200 text-gray-600">
                              Dold
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 line-clamp-2">{announcement.content}</p>
                        {announcement.cta_type !== 'none' && (
                          <div className="flex items-center gap-1 mt-2 text-xs text-amber-600">
                            {CtaIcon && <CtaIcon className="w-3 h-3" />}
                            <span>{announcement.cta_text || ctaType?.label}</span>
                          </div>
                        )}
                      </div>
                      
                      {/* Actions */}
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleToggleVisibility(announcement)}
                          title={announcement.is_visible ? 'Dölj' : 'Visa'}
                        >
                          {announcement.is_visible ? (
                            <Eye className="w-4 h-4" />
                          ) : (
                            <EyeOff className="w-4 h-4" />
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditDialog(announcement)}
                          title="Redigera"
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => handleDelete(announcement.id)}
                          title="Ta bort"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Preview Link */}
      <div className="flex justify-center">
        <a
          href="/"
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:underline flex items-center gap-1 text-sm"
        >
          <ExternalLink className="w-4 h-4" />
          Förhandsgranska startsidan
        </a>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingAnnouncement ? 'Redigera meddelande' : 'Nytt meddelande'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Titel *
              </label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="t.ex. Välkommen till vår församling"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Innehåll
              </label>
              <Textarea
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                placeholder="Kort beskrivning..."
                rows={3}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Bild
              </label>
              <div className="flex gap-2">
                <Input
                  value={formData.image_url}
                  onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                  placeholder="Bild-URL..."
                  className="flex-1"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowImagePicker(true)}
                >
                  <ImageIcon className="w-4 h-4 mr-1" />
                  Välj
                </Button>
              </div>
              {/* 1:1 Aspect Ratio Image Preview */}
              <div className="mt-2 w-32 aspect-square rounded-lg overflow-hidden bg-gray-100 border-2 border-dashed border-gray-300">
                {formData.image_url ? (
                  <img 
                    src={formData.image_url} 
                    alt="Preview" 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-400">
                    <ImageIcon className="w-8 h-8" />
                  </div>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-1">1:1 format rekommenderas</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Knapp-typ (CTA)
              </label>
              <select
                value={formData.cta_type}
                onChange={(e) => setFormData({ ...formData, cta_type: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
              >
                {CTA_TYPES.map(type => (
                  <option key={type.id} value={type.id}>{type.label}</option>
                ))}
              </select>
            </div>

            {formData.cta_type === 'url' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Länk URL
                </label>
                <Input
                  value={formData.cta_url}
                  onChange={(e) => setFormData({ ...formData, cta_url: e.target.value })}
                  placeholder="https://..."
                />
              </div>
            )}

            {formData.cta_type === 'email' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  E-postadress
                </label>
                <Input
                  type="email"
                  value={formData.cta_email}
                  onChange={(e) => setFormData({ ...formData, cta_email: e.target.value })}
                  placeholder="info@example.com"
                />
              </div>
            )}

            {formData.cta_type !== 'none' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Knapp-text
                </label>
                <Input
                  value={formData.cta_text}
                  onChange={(e) => setFormData({ ...formData, cta_text: e.target.value })}
                  placeholder="t.ex. Läs mer, Kontakta oss"
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Ordning
              </label>
              <Input
                type="number"
                value={formData.order}
                onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                min={0}
              />
              <p className="text-xs text-gray-500 mt-1">Lägre nummer visas först</p>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_visible"
                checked={formData.is_visible}
                onChange={(e) => setFormData({ ...formData, is_visible: e.target.checked })}
                className="rounded border-gray-300 text-amber-600 focus:ring-amber-500"
              />
              <label htmlFor="is_visible" className="text-sm font-medium text-gray-700">
                Synlig på startsidan
              </label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              Avbryt
            </Button>
            <Button onClick={handleSave} className="bg-amber-600 hover:bg-amber-700">
              {editingAnnouncement ? 'Uppdatera' : 'Skapa'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Image Picker Dialog */}
      <ImagePickerDialog
        open={showImagePicker}
        onClose={() => setShowImagePicker(false)}
        onSelect={handleImageSelect}
      />
    </div>
  );
};

export default FeaturedAnnouncementsManager;
